import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

import { BreadcrumbHeader, DetailPanel, StatusBadge, EmptyState } from '../../components/shared';
import { shipments, partOrders } from '../../services/dataClient';

// ─── Status progression ───────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const STATUS_STEPS = ['Created', 'Active', 'In Transit', 'Delivered'];
const NEXT_STATUS = { Active: 'In Transit', 'In Transit': 'Delivered' };

const STEP_COLORS = {
  active: { dot: '#009688', line: '#009688', label: '#009688' },
  current: { dot: '#009688', line: 'var(--grey-200)', label: '#009688' },
  future: { dot: 'var(--grey-300)', line: 'var(--grey-200)', label: 'var(--grey-400)' }
};

function getStepState(step, currentStatus) {
  const stepIdx = STATUS_STEPS.indexOf(step);
  const currentIdx = STATUS_STEPS.indexOf(currentStatus);
  if (stepIdx < currentIdx) return 'active';
  if (stepIdx === currentIdx) return 'current';
  return 'future';
}

// ─── Timeline builder ─────────────────────────────────────────────────────────
function buildTimeline(shipment, extraEvents = []) {
  const base = dayjs(shipment.createdDate);
  const events = [{ label: 'Shipment Created', date: base.format('MMM D, YYYY') }];
  if (shipment.status === 'Active' || shipment.status === 'In Transit' || shipment.status === 'Delivered') {
    events.push({ label: 'Picked Up by Carrier', date: base.add(1, 'day').format('MMM D, YYYY') });
  }
  if (shipment.status === 'In Transit' || shipment.status === 'Delivered') {
    events.push({ label: 'In Transit', date: base.add(2, 'day').format('MMM D, YYYY') });
  }
  if (shipment.status === 'Delivered') {
    events.push({ label: 'Delivered', date: base.add(5, 'day').format('MMM D, YYYY') });
  }
  return [...events, ...extraEvents];
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StatusStepper({ currentStatus }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
      {STATUS_STEPS.map((step, i) => {
        const state = getStepState(step, currentStatus);
        const colors = STEP_COLORS[state];
        const isLast = i === STATUS_STEPS.length - 1;
        return (
          <Box key={step} sx={{ display: 'flex', alignItems: 'center', flex: isLast ? 0 : 1 }}>
            {/* Dot + label */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <Box
                sx={{ width: 12, height: 12, borderRadius: '50%',
                  backgroundColor: colors.dot,
                  border: '2px solid',
                  borderColor: colors.dot,
                  flexShrink: 0
                }} />
              
              <Typography sx={{ fontSize: '0.6875rem', color: colors.label, fontWeight: state !== 'future' ? 600 : 400, whiteSpace: 'nowrap' }}>
                {step}
              </Typography>
            </Box>
            {/* Connector line */}
            {!isLast &&
            <Box
              sx={{ flex: 1,
                height: '2px',
                backgroundColor: state === 'active' ? '#009688' : 'var(--grey-200)',
                mx: '4px',
                mb: '16px'
              }} />

            }
          </Box>);

      })}
    </Box>);

}

// ─── Activity timeline ────────────────────────────────────────────────────────
function ActivityTimeline({ events }) {
  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Activity Timeline
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {events.map((ev, i) => {
          const isLast = i === events.length - 1;
          return (
            <Box key={`${ev.label}-${i}`} sx={{ display: 'flex', gap: '14px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
                <Box
                  sx={{ width: 12, height: 12, borderRadius: '50%', mt: '3px', flexShrink: 0,
                    backgroundColor: '#009688', border: '2px solid #009688'
                  }} />
                
                {!isLast &&
                <Box sx={{ width: '2px', flex: 1, minHeight: 28, backgroundColor: 'var(--grey-200)', mt: '2px' }} />
                }
              </Box>
              <Box sx={{ pb: isLast ? 0 : 3 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                  {ev.label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                  {ev.date}
                </Typography>
              </Box>
            </Box>);

        })}
      </Box>
    </ContentCard>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ShipmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const shipment = shipments.find((s) => s.id === id);
  const linkedOrder = shipment?.partOrderId ?
  partOrders.find((o) => o.id === shipment.partOrderId) :
  null;

  const [localStatus, setLocalStatus] = useState(null);
  const [extraEvents, setExtraEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNext, setSelectedNext] = useState('');
  const [showBanner, setShowBanner] = useState(false);

  const effectiveStatus = localStatus ?? shipment?.status;
  const nextStatus = NEXT_STATUS[effectiveStatus] ?? null;

  const timelineEvents = useMemo(
    () => buildTimeline(shipment ?? { createdDate: dayjs().format('YYYY-MM-DD'), status: 'Active' }, extraEvents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraEvents]
  );

  if (!shipment) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          title="Shipment not found"
          description={`No shipment with ID "${id}" exists in the system.`} />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography
            onClick={() => navigate('/shipments/dispatch')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            
            ← Back to Dispatch Board
          </Typography>
        </Box>
      </Box>);

  }

  const handleUpdateStatus = () => {
    const newEvent = {
      label: selectedNext === 'In Transit' ? 'In Transit' : 'Delivered',
      date: dayjs().format('MMM D, YYYY')
    };
    setLocalStatus(selectedNext);
    setExtraEvents((prev) => [...prev, newEvent]);
    setModalOpen(false);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
    setSelectedNext('');
  };

  const shipmentInfoFields = [
  { label: 'Shipment #', value: shipment.shipmentNumber },
  { label: 'Origin Type', value: shipment.originType },
  { label: 'Origin Location', value: shipment.originLocation },
  { label: 'Carrier', value: shipment.carrier },
  { label: 'Tracking #', value: shipment.trackingNumber },
  ...(shipment.serialNumber ? [{ label: 'Serial #', value: <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{shipment.serialNumber}</Box> }] : []),
  { label: 'Created Date', value: shipment.createdDate },
  ...(shipment.notes ? [{ label: 'Notes', value: shipment.notes }] : [])];

  const customerFields = [
  { label: 'Customer Name', value: shipment.customerName },
  { label: 'Destination Address', value: shipment.customerAddress }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            Shipment status updated successfully.
          </Typography>
        </Box>
      }

      {/* Header */}
      <BreadcrumbHeader
        title={shipment.shipmentNumber}
        subtitle={shipment.customerName}
        crumbs={[
        { label: 'Shipments' },
        { label: 'Dispatch Board', path: '/shipments/dispatch' },
        { label: shipment.shipmentNumber }]
        } />

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
          <DetailPanel title="Shipment Info" fields={shipmentInfoFields} />
          <DetailPanel title="Customer" fields={customerFields} />
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
          {/* Linked Part Order card */}
          <ContentCard
            sx={{ p: 3
            }}>
            
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
              Linked Part Order
            </Typography>
            <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />

            {linkedOrder ?
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
              { label: 'Ticket #', value: linkedOrder.ticketNumber },
              { label: 'Part #', value: linkedOrder.partNumber },
              { label: 'Customer', value: linkedOrder.customerName },
              { label: 'Status', value: <StatusBadge status={linkedOrder.status} /> }]
              .map(({ label, value }) =>
              <Box key={label} sx={{ display: 'flex', gap: 2 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 100, flexShrink: 0 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>{value}</Typography>
                  </Box>
              )}
                <Typography
                onClick={() => navigate(`/orders/parts/${linkedOrder.id}`)}
                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', mt: 1,
                  '&:hover': { textDecoration: 'underline' }
                }}>
                
                  View Order →
                </Typography>
              </Box> :

            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
            }
          </ContentCard>

          {/* Shipment Status card */}
          <ContentCard
            sx={{ p: 3
            }}>
            
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
              Shipment Status
            </Typography>
            <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <StatusBadge status={effectiveStatus} />
            </Box>

            <StatusStepper currentStatus={effectiveStatus} />

            <Box sx={{ mt: 2 }}>
              {effectiveStatus === 'Delivered' ?
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.8125rem', color: '#2E7D32', fontWeight: 600 }}>
                    Shipment complete
                  </Typography>
                </Box> :

              <Button
                variant="outlined"
                size="small"
                onClick={() => {setSelectedNext(nextStatus || '');setModalOpen(true);}}
                sx={{ textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#009688',
                  borderColor: '#009688',
                  '&:hover': { backgroundColor: '#E0F2F1', borderColor: '#009688' }
                }}>
                
                  Update Status
                </Button>
              }
            </Box>
          </ContentCard>

          {/* Activity Timeline */}
          <ActivityTimeline events={timelineEvents} />
        </Box>
      </Box>

      {/* Update Status modal */}
      <Dialog open={modalOpen} onClose={(e, reason) => {if (reason !== 'backdropClick') setModalOpen(false);}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Update Shipment Status
          <IconButton size="small" onClick={() => setModalOpen(false)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <FormControl size="small" fullWidth>
            <InputLabel>New Status</InputLabel>
            <Select
              label="New Status"
              value={selectedNext}
              onChange={(e) => setSelectedNext(e.target.value)}>
              
              {effectiveStatus === 'Active' &&
              <MenuItem value="In Transit">In Transit</MenuItem>
              }
              {(effectiveStatus === 'Active' || effectiveStatus === 'In Transit') &&
              <MenuItem value="Delivered">Delivered</MenuItem>
              }
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography
            onClick={() => setModalOpen(false)}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
            
            Cancel
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedNext}
            onClick={handleUpdateStatus}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              px: 2
            }}>
            
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>);

}